import { useState } from 'react';
import type { SessionFullDetail } from '../../../types/analyticstypes';
import SessionHeader from './SessionHeader';
import CancelledBanner from './CancelledBanner';
import OverviewTab from './OverviewTab';
import ItemsTab from './ItemsTab';
import PaymentTab from './PaymentTab';
import NgrpTab from './NgrpTab';
import ReceiptTab from './ReceiptTab';
import JourneyTab from './JourneyTab';
import SessionReplay from './SessionReplay';

type TabKey = 'overview' | 'items' | 'payment' | 'ngrp' | 'receipt' | 'journey' | 'replay';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'items', label: 'Items' },
  { key: 'payment', label: 'Payment' },
  { key: 'ngrp', label: 'NGRP' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'journey', label: 'Journey' },
  { key: 'replay', label: 'Replay' },
];

export default function SessionDetail({ detail }: { detail: SessionFullDetail }) {
  const [tab, setTab] = useState<TabKey>('overview');
  const { journey, outer, tenders, ngrp, receipt, journeyCorrupt } = detail;

  const tabsForRender = TABS.filter((t) => !(t.key === 'replay' && journeyCorrupt));

  return (
    <div>
      <SessionHeader journey={journey} outer={outer} />
      <CancelledBanner journey={journey} outer={outer} />

      {outer.ngrpTenderError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 mb-4 text-xs flex items-start gap-2">
          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <span>
            <strong>NGRP tender error.</strong> NGRP returned an error when we sent the
            tendered basket: <span className="font-mono">{outer.ngrpTenderError}</span> See
            the NGRP tab for the raw request.
          </span>
        </div>
      )}

      {journeyCorrupt && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 mb-4 text-xs flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <span>
            <strong>Journey data unavailable for this transaction.</strong> The kiosk
            journey was overwritten by a subsequent session. Order details below are
            from the order system and are accurate.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-200">
        {tabsForRender.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'overview' && <OverviewTab journey={journey} outer={outer} />}
        {tab === 'items' && <ItemsTab journey={journey} outer={outer} />}
        {tab === 'payment' && <PaymentTab tenders={tenders} outer={outer} />}
        {tab === 'ngrp' && <NgrpTab ngrp={ngrp} outer={outer} />}
        {tab === 'receipt' && <ReceiptTab receipt={receipt} />}
        {tab === 'journey' && <JourneyTab journey={journey} />}
        {tab === 'replay' && !journeyCorrupt && <SessionReplay payload={journey} />}
      </div>
    </div>
  );
}
